// external
import React, { useContext } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";

// state management
import { ClientContext } from "../context/ClientContext";
import { FaoBuilderContext } from "../context/FaoBuilderContext";

// components
import CustomButton from "./partials/custom-button/custom-button";

// constants
import { STEPS } from "pages/fao-builder/constants/faob-steps.constants";

const ModelCard = ({ model, disabled, isPublished = false }) => {
  const navigate = useNavigate();
  const { currentClientId } = useContext(ClientContext);
  const { setModelConfig } = useContext(FaoBuilderContext);
  const handleClick = async () => {
    try {
      let nextStep = "";
      setModelConfig(model);
      // direct user to the metadata page if any model metadat is missing
      if (
        !model.versionName ||
        !model.entryTerm ||
        !model.authorName ||
        !model.studentType
      ) {
        nextStep = "metadata";
      } else {
        const incompleteConfig = STEPS.filter((item) => item.value).find(
          (item) => !model.configs[item.value]
        );

        // If all steps are complete, go to the last step which is checkout
        const lastStep = STEPS.slice(-1)[0];
        nextStep = incompleteConfig ? incompleteConfig.route : lastStep.route;
      }

      navigate(
        `/clients/${currentClientId}/fao-builder/${model.id}/${nextStep}`
      );
    } catch (error) {
      console.error("Error navigating to model:", error);
    }
  };

  const renderTag = (tag) => {
    if (!tag) {
      return null;
    }
    return (
      <span className="rounded bg-gray-100 px-2 py-1 text-sm font-normal text-gray-500">
        {tag}
      </span>
    );
  };
  const formatDate = (date) => {
    return date ? new Date(parseInt(date)).toISOString().split("T")[0] : "N/A";
  };

  return (
    <CustomButton
      customClass="h-full w-full !p-0"
      onClick={handleClick}
      key={model.id}
      size="xs"
      disabled={disabled}
      aria-label={`Navigate to ${model.versionName} FAO model`}
    >
      <div
        className={classNames(
          "flex h-[400px] w-full flex-col items-start rounded-lg border border-gray-300 p-6 transition-colors",
          { "cursor-pointer hover:border-blue-03": !disabled }
        )}
      >
        <div className="flex w-full items-center justify-between">
          <div className="mb-4 flex flex-col items-start">
            <h3 className="text-ellipsis text-left text-lg font-semibold text-blue-03">
              {`${model.versionName ? model.versionName : "..."}`}
            </h3>
            <p className="text-sm font-light text-gray-500">{model.id}</p>
          </div>
          {isPublished && (
            <span className="rounded bg-gray-100 px-2 py-1 text-sm text-green-500">
              Published
            </span>
          )}
        </div>

        {(model.entryTerm || model.studentType) && (
          <div className="mb-4 flex gap-2">
            {renderTag(model.studentType)}
            {renderTag(model.entryTerm)}
          </div>
        )}

        <div className="w-full flex-grow space-y-4 overflow-auto">
          {STEPS.map((item) => {
            return item.value ? (
              <div
                key={`${model.id}_${item.value}`}
                className="flex items-center gap-3"
              >
                <div
                  className={classNames(
                    "h-4 w-4 rounded-full border-2 border-blue-03",
                    {
                      "bg-blue-03": model.configs[item.value],
                      "bg-white": !model.configs[item.value],
                    }
                  )}
                />
                <span className="text-sm font-normal text-gray-600">
                  {item.label}
                </span>
              </div>
            ) : null;
          })}
        </div>

        <div className="flex w-full flex-col items-start border-t border-gray-200 pt-4">
          <div className="mb-2 flex gap-2 text-sm text-gray-600">
            <p>Last updated:</p>
            <p className="font-normal">
              {model.lastUpdatedDate
                ? formatDate(model.lastUpdatedDate)
                : "Unknown"}
            </p>
          </div>
          <div className="mb-2 flex gap-2 text-sm text-gray-600">
            <p>Author:</p>
            <p className="font-normal">
              {model.authorName ? model.authorName : "Unknown"}
            </p>
          </div>
        </div>
      </div>
    </CustomButton>
  );
};

ModelCard.propTypes = {
  model: PropTypes.shape({
    id: PropTypes.string,
    authorName: PropTypes.string,
    configs: PropTypes.object,
    entryTerm: PropTypes.string,
    lastUpdatedDate: PropTypes.string,
    schoolKey: PropTypes.string,
    status: PropTypes.string,
    studentType: PropTypes.string,
    versionName: PropTypes.string,
  }).isRequired,
  disabled: PropTypes.bool,
  isPublished: PropTypes.bool,
};
export default ModelCard;
